import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Linkedin, Mail, ArrowRight } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UeberUnsPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/about`).then(r => setData(r.data)).catch(() => {});
  }, []);

  usePageSeo({
    title: "Über uns | LAB336",
    description: "Erfahren Sie mehr über LAB336 – Beratung für Prozessoptimierung, Digitalisierung und KI-Integration in der Schweiz.",
    canonical: `${BASE_URL}/ueber-uns`,
  });

  if (!data) return <div className="min-h-screen bg-white" />;

  return (
    <div className="min-h-screen bg-white" data-testid="ueber-uns-page">
      <header className="bg-lab-bg-dark">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>
          <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">
            Über uns
          </h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          {data.image_url && (
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden bg-lab-bg-light">
                <img
                  src={data.image_url}
                  alt={`${data.name} – ${data.title}`}
                  className="w-full h-full object-cover object-top"
                  data-testid="ueber-uns-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-lab-accent -z-10 hidden lg:block" />
            </div>
          )}

          {/* Content */}
          <div>
            <h2 className="font-playfair text-3xl font-semibold text-lab-primary mb-2" data-testid="ueber-uns-name">
              {data.name}
            </h2>
            {data.title && (
              <p className="text-lab-accent font-medium mb-6">{data.title}</p>
            )}
            <p className="text-lab-text-muted leading-relaxed mb-4">{data.paragraph_1}</p>
            <p className="text-lab-text-muted leading-relaxed mb-6">{data.paragraph_2}</p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 py-6 border-y border-lab-border mb-6">
              <div>
                <p className="font-playfair text-2xl font-semibold text-lab-primary">{data.stat_1_value}</p>
                <p className="text-sm text-lab-text-muted mt-1">{data.stat_1_label}</p>
              </div>
              <div>
                <p className="font-playfair text-2xl font-semibold text-lab-primary">{data.stat_2_value}</p>
                <p className="text-sm text-lab-text-muted mt-1">{data.stat_2_label}</p>
              </div>
              <div>
                <p className="font-playfair text-2xl font-semibold text-lab-primary">{data.stat_3_value}</p>
                <p className="text-sm text-lab-text-muted mt-1">{data.stat_3_label}</p>
              </div>
            </div>

            {/* Links */}
            <div className="flex items-center gap-4">
              <Link
                to="/#scheduler"
                className="inline-flex items-center gap-2 bg-lab-primary text-white px-6 py-3 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors"
                data-testid="ueber-uns-cta"
              >
                Termin vereinbaren <ArrowRight className="w-4 h-4" />
              </Link>
              {data.linkedin_url && (
                <a
                  href={data.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-lab-border flex items-center justify-center text-lab-text-muted hover:text-lab-accent hover:border-lab-accent transition-colors"
                  data-testid="ueber-uns-linkedin"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {data.email && (
                <a
                  href={`mailto:${data.email}`}
                  className="w-10 h-10 border border-lab-border flex items-center justify-center text-lab-text-muted hover:text-lab-accent hover:border-lab-accent transition-colors"
                  data-testid="ueber-uns-email"
                >
                  <Mail className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} LAB 336. Alle Rechte vorbehalten.
          </p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm transition-colors">
            Startseite
          </Link>
        </div>
      </footer>
    </div>
  );
}
